import type { FC, ChangeEvent } from 'react';
import { twMerge } from 'tailwind-merge';
import { CheckSolidGlyph, MinusSolidGlyph } from '@acast-tech/decibel-glyphs';

interface CheckboxProps {
  name: string;
  checked: boolean;
  indeterminate?: boolean;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  className?: string;
  disabled?: boolean;
  color?: string;
}

/**
 * This is the checkbox component.
 * @param name - The name of the checkbox. This is used to identify the checkbox in the form.
 * @param checked - Whether the checkbox is checked. This is used to determine the state of the checkbox.
 * @param indeterminate - Whether the checkbox is indeterminate. This is used to determine the state of the checkbox.
 * @param onChange - The function to call when the checkbox is changed. This is used to update the state of the checkbox.
 * @param className - The class name to apply to the checkbox. This is used to apply additional styles to the checkbox.
 * @param disabled - Whether the checkbox is disabled. This is used to disable the checkbox.
 * @param color - The color of the checkbox. This is used to set the color of the checkbox.
 * @returns The checkbox component.
 */
const Checkbox: FC<CheckboxProps> = ({
  name,
  checked,
  indeterminate,
  onChange,
  className,
  disabled,
  color = '#4040A8',
}) => {
  const isActive = checked || indeterminate;
  const isChecked = checked && !indeterminate;
  return (
    <div
      className={twMerge('relative inline-flex items-center', className)}
      data-testid="checkbox-container"
    >
      <input
        type="checkbox"
        name={name}
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
        aria-checked={indeterminate ? 'mixed' : checked}
      />

      {/* Custom checkbox visual */}
      <div
        data-testid="checkbox-visual"
        className={twMerge(
          'rounded cursor-pointer transition-all duration-300 flex items-center justify-center p-0.5 border size-4',
          disabled && 'opacity-50 cursor-not-allowed'
        )}
        style={{
          backgroundColor: isActive ? color : 'transparent',
          borderColor: isActive ? color : '#646464',
        }}
      >
        {/* Checkmark */}
        {isChecked && <CheckSolidGlyph className="size-4 fill-white" data-testid="check-icon" />}
        {indeterminate && (
          <MinusSolidGlyph className="size-4 fill-white" data-testid="minus-icon" />
        )}
      </div>
    </div>
  );
};

export default Checkbox;
